import { FC, useState, useMemo } from 'react';
import {
  Table,
  Text,
  Loader,
  Box,
  Center,
  Paper,
  Group,
  ActionIcon,
  Divider,
} from '@mantine/core';
import { IconCheck, IconX, IconX as IconClose } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { usePatient } from '@/hooks/usePatient';
import { usePatientEncounters } from '@/hooks/usePatientEncounters';
import { useMedplum } from '@medplum/react';
import { useDateDisplay } from '@/hooks/useDateDisplay';
import { usePsychomotorDevelopmentData } from '@/hooks/usePsychomotorDevelopmentData';
import { loadEncounterPsychomotorDevelopment } from '../examination/examination.helpers';
import type { Encounter } from '@medplum/fhirtypes';
import { SummaryTabBlockChildProps } from './SummaryTab';
import { PsychomotorDevelopmentListItem } from '../examination/psychomotor-development/PsychomotorDevelopmentListItem';
import {
  PSYCHOMOTOR_DEVELOPMENT_TYPES,
  PsychomotorDevelopmentItem,
} from '@/types/psychomotor-development.types';
import classes from './PsychomotorProgress.module.css';

export const PsychomotorProgress: FC<SummaryTabBlockChildProps> = ({
  minimizedView = false,
}) => {
  const { t } = useTranslation('patient-pages');
  const patient = usePatient();
  const medplum = useMedplum();
  const { encounters, loading: encountersLoading } =
    usePatientEncounters(patient);
  const { encountersData, loading } = usePsychomotorDevelopmentData(
    patient,
    encounters,
    encountersLoading,
    medplum
  );
  const [selectedEncounter, setSelectedEncounter] = useState<
    Encounter | undefined
  >();
  const [selectedEncounterItems, setSelectedEncounterItems] = useState<
    PsychomotorDevelopmentItem[]
  >([]);
  const [loadingSelectedEncounter, setLoadingSelectedEncounter] =
    useState(false);

  // Date formatter for encounter dates
  const formatDate = useDateDisplay();

  // Sort encounters by date (ascending - oldest first)
  const sortedEncountersData = useMemo(() => {
    const sorted = [...encountersData].sort((a, b) => {
      const dateA = new Date(a.encounter.period?.start ?? '').getTime();
      const dateB = new Date(b.encounter.period?.start ?? '').getTime();
      return dateA - dateB;
    });

    // In minimized view, show only the 4 most recent encounters
    if (minimizedView) {
      return sorted.slice(-4);
    }

    return sorted;
  }, [encountersData, minimizedView]);

  // Handle click on encounter date
  const handleEncounterClick = async (encounter: Encounter) => {
    if (!patient) {
      return;
    }

    // If clicking the same encounter, close it
    if (selectedEncounter?.id === encounter.id) {
      setSelectedEncounter(undefined);
      setSelectedEncounterItems([]);
      return;
    }

    setSelectedEncounter(encounter);
    setLoadingSelectedEncounter(true);

    try {
      const { items } = await loadEncounterPsychomotorDevelopment(
        medplum,
        patient,
        encounter
      );
      setSelectedEncounterItems(items);
    } catch (error) {
      console.error('Error loading encounter psychomotor development:', error);
      setSelectedEncounterItems([]);
    } finally {
      setLoadingSelectedEncounter(false);
    }
  };

  // Handle close detailed view
  const handleCloseDetailedView = () => {
    setSelectedEncounter(undefined);
    setSelectedEncounterItems([]);
  };

  if (loading || encountersLoading) {
    return (
      <Center h={200}>
        <Loader size="sm" />
      </Center>
    );
  }

  if (encountersData.length === 0) {
    return (
      <Center h={200}>
        <Text size="sm" c="dimmed">
          {t('psychomotorDevelopment.noAssessment', {
            defaultValue: 'No psychomotor development assessment recorded',
          })}
        </Text>
      </Center>
    );
  }

  return (
    <Box>
      <Box className={classes.tableContainer}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th
                pos="sticky"
                left={0}
                className={classes.stickyColumn}
                bg="var(--mantine-color-body)"
                miw={180}
              >
                <Text size="sm" fw={600}>
                  {t('psychomotorDevelopment.itemTitle', {
                    defaultValue: 'Milestone',
                  })}
                </Text>
              </Table.Th>
              {sortedEncountersData.map((encounterData) => {
                const isSelected =
                  selectedEncounter?.id === encounterData.encounter.id;
                return (
                  <Table.Th
                    key={encounterData.encounter.id}
                    className={
                      minimizedView
                        ? classes.encounterHeaderDisabled
                        : classes.encounterHeader
                    }
                    bg={isSelected ? 'blue.0' : 'transparent'}
                    onClick={
                      minimizedView
                        ? undefined
                        : () => handleEncounterClick(encounterData.encounter)
                    }
                    onMouseEnter={
                      minimizedView
                        ? undefined
                        : (e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor =
                                'var(--mantine-color-gray-1)';
                            }
                          }
                    }
                    onMouseLeave={
                      minimizedView
                        ? undefined
                        : (e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor =
                                'transparent';
                            } else {
                              e.currentTarget.style.backgroundColor =
                                'var(--mantine-color-blue-0)';
                            }
                          }
                    }
                  >
                    <Text
                      size="xs"
                      ta="center"
                      c={minimizedView ? undefined : 'blue'}
                      fw={500}
                      className={classes.encounterDateText}
                    >
                      {formatDate(encounterData.encounter.period?.start)}
                    </Text>
                  </Table.Th>
                );
              })}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {PSYCHOMOTOR_DEVELOPMENT_TYPES.map((type) => (
              <Table.Tr key={type}>
                <Table.Td
                  pos="sticky"
                  left={0}
                  className={classes.stickyColumn}
                  bg="var(--mantine-color-body)"
                  miw={180}
                >
                  <Text size="sm">
                    {t(`psychomotorDevelopment.${type}`, {
                      defaultValue: type,
                    })}
                  </Text>
                </Table.Td>
                {sortedEncountersData.map((encounterData) => {
                  const value = encounterData.data[type];
                  const isSelected =
                    selectedEncounter?.id === encounterData.encounter.id;
                  return (
                    <Table.Td
                      key={encounterData.encounter.id}
                      bg={isSelected ? 'blue.0' : 'transparent'}
                    >
                      <Center>
                        {value === true ? (
                          <IconCheck size={18} color="green" />
                        ) : value === false ? (
                          <IconX size={18} color="red" />
                        ) : (
                          <Paper w={18} h={18} bg="gray.4" radius="xs" />
                        )}
                      </Center>
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      {/* Legend */}
      <Group mt="xs" gap="md" justify="center">
        <Group gap={4} align="center">
          <IconCheck size={14} color="green" />
          <Text size="xs" c="dimmed">
            {t('psychomotorDevelopment.yes', { defaultValue: 'Yes' })}
          </Text>
        </Group>
        <Group gap={4} align="center">
          <IconX size={14} color="red" />
          <Text size="xs" c="dimmed">
            {t('psychomotorDevelopment.no', { defaultValue: 'No' })}
          </Text>
        </Group>
        <Group gap={4} align="center">
          <Paper w={14} h={14} bg="gray.4" radius="xs" />
          <Text size="xs" c="dimmed">
            {t('psychomotorDevelopment.notAnswered', {
              defaultValue: 'Not answered',
            })}
          </Text>
        </Group>
      </Group>

      {/* Detailed view below the table */}
      {selectedEncounter && patient && (
        <Box mt="md">
          <Divider />
          <Group justify="space-between" my="md">
            <Text fw={700} size="lg">
              {t('psychomotorDevelopment.title', {
                ns: 'patient-pages',
                defaultValue: 'Psychomotor Development',
              })}{' '}
              - {formatDate(selectedEncounter.period?.start)}
            </Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={handleCloseDetailedView}
              aria-label="Close"
            >
              <IconClose size={20} />
            </ActionIcon>
          </Group>
          {loadingSelectedEncounter ? (
            <Center h={200}>
              <Loader size="sm" />
            </Center>
          ) : (
            <PsychomotorDevelopmentListItem
              items={selectedEncounterItems}
              onAction={() => {}} // No actions needed in readonly mode
              isDeleting={false}
              readonly
            />
          )}
        </Box>
      )}
    </Box>
  );
};
